import React from "react";
import { Box, Button, TextField, Tooltip } from "@mui/material";
import BrokenImageOutlinedIcon from "@mui/icons-material/BrokenImageOutlined";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import { resolveIconUrl } from "../../../utilities/cdnUtils";
import IconPickerDialog from "./IconPickerDialog";

interface IconFieldProps {
  label: string;
  value?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Selected org, so the picker lists and uploads against the right tenant. */
  orgId?: number | null;
  /** Shows the icon without letting it be changed — no typing, no picker. */
  disabled?: boolean;
}

/**
 * Icon path field with a live thumbnail.
 *
 * The stored value is a CDN-relative path (`public-icons/…`), not an absolute
 * URL: `resolveIconUrl` resolves it against the environment's CDN base, and the
 * SLD exporter only rewrites `public-icons/…` / `icons/…` hrefs — an absolute
 * URL would silently lose its icon in the QGIS package. The preview resolves the
 * same way the map does, so a wrong path is visible here rather than on the map.
 */
const IconField: React.FC<IconFieldProps> = ({ label, value, onChange, placeholder, orgId, disabled }) => {
  const [failed, setFailed] = React.useState(false);
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const src = resolveIconUrl(value);

  React.useEffect(() => setFailed(false), [src]);

  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <Tooltip title={value ? value : "No icon set"}>
        <Box
          sx={{
            width: 40,
            height: 40,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: "1px solid",
            borderColor: "divider",
            borderRadius: "8px",
            bgcolor: "action.hover",
            color: "text.disabled",
            overflow: "hidden"
          }}
        >
          {!src || failed ? (
            React.createElement(src && failed ? BrokenImageOutlinedIcon : ImageOutlinedIcon, { fontSize: "small" })
          ) : (
            <Box
              component="img"
              src={src}
              alt=""
              onError={() => setFailed(true)}
              sx={{ maxWidth: "100%", maxHeight: "100%", objectFit: "contain" }}
            />
          )}
        </Box>
      </Tooltip>
      <TextField
        fullWidth
        size="small"
        label={label}
        value={value ?? ""}
        placeholder={placeholder ?? "public-icons/imw/Camp.png"}
        disabled={disabled}
        onChange={(e) => onChange(e.target.value)}
      />
      {!disabled && (
        <Button size="small" onClick={() => setPickerOpen(true)} sx={{ flexShrink: 0 }} aria-label={`Choose ${label}`}>
          Choose
        </Button>
      )}
      {/*
        Mounted only while open. A point table renders one IconField per category
        plus the default, and each dialog runs an icon-library query — mounting
        them all would fire a query per row for a dialog nobody opened.
      */}
      {pickerOpen && (
        <IconPickerDialog
          open
          onClose={() => setPickerOpen(false)}
          onSelect={onChange}
          value={value}
          orgId={orgId}
        />
      )}
    </Box>
  );
};

export default IconField;
